// React 18+ with Vite doesn't require explicit React import for JSX
import { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WorkflowProvider } from './contexts/WorkflowContext';
import { PermissionProvider } from './contexts/PermissionContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Login from './components/Auth/Login';
import Layout from './components/Layout/Layout';
import ErrorBoundary from './components/Common/ErrorBoundary';
import PageSkeleton from './components/Common/PageSkeleton';
import { OfflineIndicator } from './components/Common/OfflineIndicator';
import { lazyWithChunkReload } from './utils/lazyWithChunkReload';

// Lazy load components for code splitting (reload once on stale chunk after deploy)
const Dashboard = lazyWithChunkReload(() => import('./pages/Dashboard'));
const Patients = lazyWithChunkReload(() => import('./pages/Patients'));
const Doctors = lazyWithChunkReload(() => import('./pages/Doctors'));
const Appointments = lazyWithChunkReload(() => import('./pages/Appointments'));
const Claims = lazyWithChunkReload(() => import('./pages/Claims'));
const Admissions = lazyWithChunkReload(() => import('./pages/Admissions'));
const AdminSettings = lazyWithChunkReload(() => import('./pages/AdminSettings'));
const AuditLogs = lazyWithChunkReload(() => import('./pages/AuditLogs'));
const InsuranceManagement = lazyWithChunkReload(() => import('./pages/InsuranceManagement'));
const InsuranceVerification = lazyWithChunkReload(() => import('./pages/InsuranceVerification'));
const InsuranceAgreements = lazyWithChunkReload(() => import('./pages/InsuranceAgreements'));
const InsuranceDeductionDemo = lazyWithChunkReload(() => import('./pages/InsuranceDeductionDemo'));
const ScheduleManagement = lazyWithChunkReload(() => import('./pages/ScheduleManagement'));
const Departments = lazyWithChunkReload(() => import('./pages/Departments'));
const WorkflowRoutes = lazyWithChunkReload(() => import('./pages/WorkflowRoutes'));
const Staff = lazyWithChunkReload(() => import('./pages/Staff'));
const Pharmacy = lazyWithChunkReload(() => import('./pages/Pharmacy'));
const Laboratory = lazyWithChunkReload(() => import('./pages/Laboratory'));
const Radiology = lazyWithChunkReload(() => import('./pages/Radiology'));
const LabOrderApproval = lazyWithChunkReload(() => import('./pages/LabOrderApproval'));
const PurchaseOrders = lazyWithChunkReload(() => import('./pages/PurchaseOrders'));
const MedicalRecords = lazyWithChunkReload(() => import('./components/Treatments/MedicalRecords'));
const Profile = lazyWithChunkReload(() => import('./pages/Profile'));
const LabOrderDetail = lazyWithChunkReload(() => import('./pages/LabOrderDetail'));
const RoleManagement = lazyWithChunkReload(() => import('./pages/RoleManagement'));
const PermissionManagement = lazyWithChunkReload(() => import('./pages/PermissionManagement'));
const WardManagement = lazyWithChunkReload(() => import('./pages/WardManagement'));
const Reports = lazyWithChunkReload(() => import('./pages/Reports'));

const TreatmentTemplates = lazyWithChunkReload(() => import('./pages/TreatmentTemplates'));

// Helper function to get default route based on user role
const getDefaultRoute = (role?: string): string => {
  switch (role) {
    case 'ADMIN':
      return '/patients';
    case 'DOCTOR':
      return '/workflow/triage';
    case 'PHARMACIST':
      return '/pharmacy';
    case 'LAB_TECHNICIAN':
    case 'RADIOLOGIST':
      return '/radiology/queue';
    case 'RECEPTIONIST':
      return '/patients';
    case 'REPORTS':
      return '/reports/doctors';
    default:
      return '/dashboard';
  }
};

// Wrapper component to access auth context and pass user role to PageSkeleton
// Dashboard redirect wrapper for administrators, pharmacists, doctors, lab technicians, and receptionists
const DashboardOrRedirect: React.FC = () => {
  const { user } = useAuth();
  
  // Redirect administrators to patients page
  if (user?.role === 'ADMIN') {
    return <Navigate to="/patients" replace />;
  }
  
  if (user?.role === 'PHARMACIST') {
    return <Navigate to="/pharmacy" replace />;
  }
  
  if (user?.role === 'DOCTOR') {
    return <Navigate to="/workflow/triage" replace />;
  }
  
  if (user?.role === 'LAB_TECHNICIAN' || user?.role === 'RADIOLOGIST') {
    return <Navigate to="/radiology/queue" replace />;
  }
  
  if (user?.role === 'RECEPTIONIST') {
    return <Navigate to="/patients" replace />;
  }

  if (user?.role === 'REPORTS') {
    return <Navigate to="/reports/doctors" replace />;
  }
  
  return <Dashboard />;
};

// Default route redirect component that checks user role
const DefaultRouteRedirect: React.FC = () => {
  const { user } = useAuth();
  const defaultRoute = getDefaultRoute(user?.role);
  return <Navigate to={defaultRoute} replace />;
};

const AppContent: React.FC = () => {
  const { user } = useAuth();
  
  return (
    <Suspense fallback={<PageSkeleton userRole={user?.role} />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        
        {/* Protected Routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<DashboardOrRedirect />} />
          <Route path="patients/*" element={<Patients />} />
          <Route path="doctors/*" element={<Doctors />} />
          <Route path="staff/*" element={<Staff />} />
          <Route path="appointments/*" element={<Appointments />} />
          <Route path="workflow/*" element={<WorkflowRoutes />} />
          <Route path="pharmacy/*" element={<Pharmacy />} />
          <Route path="laboratory/*" element={<Laboratory />} />
          <Route path="radiology/*" element={<Radiology />} />
          <Route path="lab/*" element={<Laboratory />} />
          <Route path="lab/orders/:orderId" element={<LabOrderDetail />} />
          <Route path="lab/approvals" element={<LabOrderApproval />} />
          <Route path="purchase-orders/*" element={<PurchaseOrders />} />
          <Route path="medical-records" element={<MedicalRecords />} />
          <Route path="profile" element={<Profile />} />
          <Route path="claims/*" element={<Claims />} />
          <Route path="insurance-verification" element={<InsuranceVerification />} />
          <Route path="admissions/*" element={<Admissions />} />
          <Route path="ward-management" element={<WardManagement />} />
          
          <Route path="admin/settings/*" element={<AdminSettings />} />
          <Route path="admin/audit" element={<AuditLogs />} />
          <Route path="admin/insurance" element={<InsuranceManagement />} />
          <Route path="admin/insurance-agreements" element={<InsuranceAgreements />} />
          <Route path="admin/departments" element={<Departments />} />
          <Route path="admin/roles" element={<RoleManagement />} />
          <Route path="admin/permissions" element={<PermissionManagement />} />
          <Route path="admin/treatments" element={<TreatmentTemplates />} />
          <Route path="schedule-management" element={<ScheduleManagement />} />
          <Route path="insurance-deduction-demo" element={<InsuranceDeductionDemo />} />
          <Route path="reports/*" element={<Reports />} />
          
          {/* Redirect /settings to /admin/settings */}
          <Route path="settings" element={<Navigate to="/admin/settings" replace />} />
          
          <Route path="" element={<DefaultRouteRedirect />} />
        </Route>
        
        {/* Default redirect */}
        <Route path="/" element={<DefaultRouteRedirect />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <PermissionProvider>
            <WorkflowProvider>
              <div className="App">
                <OfflineIndicator />
                <AppContent />
                
                <ToastContainer
                  position="top-right"
                  autoClose={5000}
                  hideProgressBar={false}
                  newestOnTop={false}
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                />
              </div>
            </WorkflowProvider>
          </PermissionProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
